import { localizeApiMessage } from "../../errors/messages";
import {
  BranchConflictError,
  BranchConflictField,
  BranchErrorResponse,
  BranchReadinessItem,
  BranchReadinessSummary,
  RouteNotReadyError,
  RouteReadiness,
} from "../../types/branch.type";
import { BranchFormErrors, isBranchFormField } from "./branchForm";
import {
  CONFLICT_FIELD_ERROR,
  CONFLICT_FIELD_LABEL,
  ChipColor,
  ITEM_STATUS_LABEL,
  OPEN_ROUTE_FAILED_MESSAGE,
  READINESS_GUIDE,
  ROUTE_OPEN_LABEL,
  ROUTE_TEST_MODE_LABEL,
} from "./constants";

const CONFLICT_STATUS = 409;

export interface StatusChipView {
  label: string;
  color: ChipColor;
}

/**
 * The สถานะความพร้อม of a whole branch: head office first (it does not sell), then a
 * Keycloak lookup that failed, then the count of REQUIRED items still missing.
 */
export const resolveReadinessStatus = (
  summary: BranchReadinessSummary,
  isHeadOffice: boolean,
): StatusChipView => {
  if (isHeadOffice) return { label: "สำนักงานใหญ่", color: "default" };
  if (summary.required_unknown_count > 0) {
    return { label: "ตรวจสอบผู้ใช้ไม่ได้", color: "default" };
  }
  if (summary.required_missing_count === 0) return { label: "พร้อม", color: "success" };
  return { label: `ขาด ${summary.required_missing_count} รายการ`, color: "warning" };
};

export const resolveRouteMode = (route: RouteReadiness): StatusChipView =>
  route.is_test_mode
    ? { label: ROUTE_TEST_MODE_LABEL, color: "warning" }
    : { label: ROUTE_OPEN_LABEL, color: "success" };

/** A missing REQUIRED item blocks เปิดใช้, so it reads louder than a recommended one. */
const itemStatusColor = (item: BranchReadinessItem): ChipColor => {
  if (item.status === "PASS") return "success";
  if (item.status === "UNKNOWN") return "default";
  return item.level === "REQUIRED" ? "error" : "warning";
};

export const resolveItemStatus = (item: BranchReadinessItem): StatusChipView => {
  const label = ITEM_STATUS_LABEL[item.status];
  return {
    label: item.count === null ? label : `${label} (${item.count})`,
    color: itemStatusColor(item),
  };
};

export interface BranchSaveFailure {
  message: string;
  fieldErrors: BranchFormErrors;
}

/**
 * Why a create/update was refused. A 409 that names fields lists them and marks the ones
 * the form holds; `branch_code` is NN + AA together, so it is only named in the message.
 */
export const resolveBranchSaveFailure = (
  status: number | undefined,
  body: BranchErrorResponse<BranchConflictError> | undefined,
  fallbackMessage: string,
): BranchSaveFailure => {
  const namedFields = body?.error?.fields;
  const conflictFields: BranchConflictField[] =
    status === CONFLICT_STATUS && Array.isArray(namedFields) ? namedFields : [];

  if (conflictFields.length > 0) {
    const fieldErrors: BranchFormErrors = {};
    conflictFields.forEach((field) => {
      if (isBranchFormField(field)) fieldErrors[field] = CONFLICT_FIELD_ERROR;
    });
    const labels = conflictFields.map((field) => CONFLICT_FIELD_LABEL[field]);
    return {
      message: `${CONFLICT_FIELD_ERROR}: ${labels.join(", ")}`,
      fieldErrors,
    };
  }

  return {
    message: body?.message ? localizeApiMessage(body.message) : fallbackMessage,
    fieldErrors: {},
  };
};

export interface RouteOpenFailure {
  message: string;
  /** One Thai line per REQUIRED item the gate found missing or unverifiable. */
  blockingItems: string[];
}

const describeBlockingItem = (item: BranchReadinessItem) =>
  `${READINESS_GUIDE[item.key].label} — ${ITEM_STATUS_LABEL[item.status]}`;

export const resolveRouteOpenFailure = (
  status: number | undefined,
  body: BranchErrorResponse<RouteNotReadyError> | undefined,
): RouteOpenFailure => {
  const message = body?.message ? localizeApiMessage(body.message) : OPEN_ROUTE_FAILED_MESSAGE;
  if (status !== CONFLICT_STATUS || !body?.error) {
    return { message, blockingItems: [] };
  }
  const { branch_items = [], route_items = [] } = body.error;
  return {
    message,
    blockingItems: [...branch_items, ...route_items].map(describeBlockingItem),
  };
};
